import { Fragment, useCallback, useEffect, useRef, useState } from 'react'

import { Listbox, ListboxButton, ListboxOption, ListboxOptions, Transition } from '@headlessui/react'
import { clsx } from 'clsx'
import { IconChevronDown, IconChevronUp, IconFilterLight } from 'packages/component-library/dist'

interface SelectItem {
    id: string;
    name: string;
}

interface SelectProductMobileProps {
    defaultValue: SelectItem,
    options: (SelectItem)[],
    accentColor?: string,
    name?: string,
    placeholder?: string,
    onChange?: (option: SelectItem) => void,
  }

export const SelectProductMobile = ({ 
  defaultValue,
  options,
  name = '',
  placeholder = '',
  accentColor = '',
  onChange: onChangeProps,
}: SelectProductMobileProps) => {
  const [value, setValue] = useState<SelectItem>(defaultValue)
  
  const onChange = useCallback((option: SelectItem) => {
    setValue(option)
    onChangeProps?.(option)
  }, [])
  
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [buttonWidth, setButtonWidth] = useState<number | null>(null)

  useEffect(() => {
    if (buttonRef.current) {
      setButtonWidth(buttonRef.current.offsetWidth)
    }
  }, [])

  return (
    <Listbox
      value={value}
      defaultValue={defaultValue}
      onChange={onChange}
      name={name}
    >
      {({ open }) => (
        <>
          <ListboxButton
            ref={buttonRef}
            className={clsx(
              'flex justify-between items-center w-full cursor-pointer rounded text-left bg-neutral-05 dark:bg-neutral-95',
              'h-10 text-body2 pl-3 py-2 border',
              open && !accentColor ? 'border-accent-60 dark:border-accent-40' : ' border-neutral-40 dark:border-neutral-70',
            )}
            style={open ? { borderColor: accentColor } : {}}
          >
            <span className="mr-2 text-neutral-70 dark:text-neutral-40"><IconFilterLight width={16} height={16} /></span>
            <span className="truncate text-neutral-98 dark:text-neutral-05">{value?.name || placeholder}</span>
            <span className="pr-3 ml-auto text-neutral-70 dark:text-neutral-40">{open ? <IconChevronUp width={16} height={16} /> : <IconChevronDown width={16} height={16} />}</span>
          </ListboxButton>
          <Transition
            show={open}
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="absolute flex flex-col shadow-[0_3px_6px_0_rgba(0,0,0,0.18)] rounded z-50 mt-24" style={{ width: buttonWidth ?? 'auto' }}>
              <ListboxOptions className="z-50 w-full overflow-x-hidden overflow-y-auto rounded max-h-60 focus:outline-none">
                {options.map((option: SelectItem) => (
                  <ListboxOption
                    key={option.id}
                    className={
                      clsx(
                        'hover:text-neutral-98 dark:hover:text-neutral-10',
                        (value?.id === option.id
                          ? 'text-neutral-98 dark:text-neutral-10'
                          : 'text-neutral-70 dark:text-neutral-40'),
        
                        'hover:bg-neutral-05 dark:hover:bg-neutral-90',
                        (value?.id === option.id
                          ? ('bg-neutral-10 dark:bg-neutral-80')
                          : 'bg-white dark:bg-neutral-95'),
                        'cursor-pointer select-none py-1.5 px-4 text-body2 break-words',
                      )
                    }
                    value={option}
                  >
                    {option.name}
                  </ListboxOption>
                ))}
              </ListboxOptions>
            </div>
          </Transition>
        </>
      )}
    </Listbox>
  )
}